import type { AwardState } from '../lifecycle/index.js';
import type { SpotApprovalMode } from './policy.js';
import type { SpotReviewStage } from './review.js';

/**
 * APP-226 (Tom: "we should show where they're at in the system and if they need to be unblocked by
 * a manager or if there was no manager set"): the All Spot awards view's plain stage for one
 * nominee. Words are the web's; these are the stages the filter and the rows share. APP-233:
 * `funding` is a decision held while the Executives decide a funding shortfall, so the stage says
 * what's blocking, as who it waits on does.
 */
export const SPOT_QUEUE_STAGES = [
  'committee',
  'funding',
  'manager',
  'no_manager',
  'letter',
  'released',
  'payroll',
  'paid',
  'closed',
  'not_awarded',
  'cancelled',
] as const;
export type SpotQueueStage = (typeof SPOT_QUEUE_STAGES)[number];

/**
 * APP-232: the stages still in progress, in order: every stage but Closed, Not awarded and
 * Cancelled. Home's "Spot awards in progress" counts these, and (APP-233) All Spot awards opens on
 * them.
 */
export const SPOT_IN_PROGRESS_STAGES = [
  'committee',
  'funding',
  'manager',
  'no_manager',
  'letter',
  'released',
  'payroll',
  'paid',
] as const satisfies readonly SpotQueueStage[];
export type SpotInProgressStage = (typeof SPOT_IN_PROGRESS_STAGES)[number];

/** Whether a nominee at this stage is still in progress (APP-232). */
export function spotInProgress(stage: SpotQueueStage): stage is SpotInProgressStage {
  return (SPOT_IN_PROGRESS_STAGES as readonly SpotQueueStage[]).includes(stage);
}

/** The review stages where a nominee still waits on a decision: always in progress. */
export const SPOT_WAITING_REVIEW_STAGES = [
  'committee_pending',
  'supervisor_pending',
] as const satisfies readonly SpotReviewStage[];

/**
 * SENT-132: the review stage a Spot funding request waits in: the decision it was asked for, the
 * supervisor's, or the committee's when it decides alone (`committee_only`). A nomination sent
 * before review policies were saved decides as the supervisor's.
 */
export function spotFundingWaitStage(
  approvalMode: SpotApprovalMode | null | undefined,
): SpotReviewStage {
  return approvalMode === 'committee_only' ? 'committee_pending' : 'supervisor_pending';
}

/**
 * APP-243: who opened a funding request. `asked`: someone sent the shortfall to the Executives
 * before a decision, as every request before the redesign was. `automatic` (Tom, Q25 = A): MyRewards
 * opened it when an approved Spot award left its pool short, since a short pool never holds one.
 */
export type SpotFundingOrigin = 'asked' | 'automatic';

/**
 * SENT-132: whether a funding request stored as `pending` still waits on the Executives. An asked
 * one waits while its nominee still waits on the decision it was asked for. Once the nominee leaves
 * that stage (cancelled, vetoed, declined, or approved), the request stays `pending` in storage but
 * waits on no one: no Executive can decide it, and no reader may count it as waiting.
 *
 * APP-243: an automatic one is about its pool and fiscal year, not one colleague: it waits while
 * that pool is still short (`poolShort`, read from the ledger now by the caller), whatever became
 * of the award that opened it, and while it's the newest automatic request still stored pending
 * for that pool and year (`latest`): a later one, opened when the pool went short again, stands in
 * for it, so one top-up is asked for at a time. Topped up some other way, or stood in for, it
 * waits on no one, as SENT-132's do. Every reader of pending requests asks this, so none can drift.
 */
export function spotFundingWaits(input: {
  approvalMode: SpotApprovalMode | null | undefined;
  reviewStage: SpotReviewStage;
  /** Who opened it; `asked` when not said. */
  origin?: SpotFundingOrigin;
  /** An automatic request's pool and fiscal year are short now. */
  poolShort?: boolean;
  /** An automatic request is the newest still pending for its pool and year. */
  latest?: boolean;
}): boolean {
  if (input.origin === 'automatic') return input.poolShort === true && input.latest === true;
  return input.reviewStage === spotFundingWaitStage(input.approvalMode);
}

/** An awarded nominee's award line states where it has ended: closed or cancelled. */
export const SPOT_ENDED_LINE_STATES = [
  'S10',
  'S11',
  'X1',
  'X2',
] as const satisfies readonly AwardState[];

/**
 * APP-232 (SENT-131): `spotInProgress(spotQueueStage(…))` from the stored facts alone, so a query
 * can keep only nominees in progress before reading them: waiting on a decision, or awarded with a
 * line that hasn't closed or been cancelled. The supervisor and a funding request don't matter here
 * (manager, no manager and funding are all in progress). A domain test keeps the two equal for every
 * stage, line state, supervisor and funding request.
 */
export function spotInProgressFacts(input: {
  reviewStage: SpotReviewStage;
  lineState: AwardState | null;
}): boolean {
  if ((SPOT_WAITING_REVIEW_STAGES as readonly SpotReviewStage[]).includes(input.reviewStage))
    return true;
  return (
    input.reviewStage === 'awarded' &&
    (input.lineState === null ||
      !(SPOT_ENDED_LINE_STATES as readonly AwardState[]).includes(input.lineState))
  );
}

/**
 * Who the nominee's next step waits on. `stock_admin` (APP-247, change 17): the Stock Admin team
 * checks and releases the letter. `holder` hands out a released letter.
 */
export const SPOT_QUEUE_WAITING = [
  'committee',
  'manager',
  'no_manager',
  'executives',
  'stock_admin',
  'holder',
  'payroll',
  'none',
] as const;
export type SpotQueueWaiting = (typeof SPOT_QUEUE_WAITING)[number];

/**
 * A nominee's stage from their review stage and, once awarded, their award line's state. Waiting
 * on the manager with nobody who can decide (no active manager and no appointment) is "No manager
 * set": a System Admin or Stock Admin appoints one (APP-227).
 *
 * APP-233 (Tom: the badge "should say what's actually blocking"): a decision held by a funding
 * shortfall sent to the Executives, the committee's or the manager's, is `funding` until an
 * Executive answers. "No manager set" still wins: an Executive's answer wouldn't unblock it, since
 * nobody could then decide.
 */
export function spotQueueStage(input: {
  reviewStage: SpotReviewStage;
  /** Someone can decide at the supervisor stage now (`currentSpotSupervisor`). */
  hasSupervisor: boolean;
  /** A funding shortfall waits on the Executives for this nominee. */
  fundingPending: boolean;
  /** The award line's state, once there is an award. */
  lineState: AwardState | null;
}): SpotQueueStage {
  switch (input.reviewStage) {
    case 'committee_pending':
      return input.fundingPending ? 'funding' : 'committee';
    case 'supervisor_pending':
      if (!input.hasSupervisor) return 'no_manager';
      return input.fundingPending ? 'funding' : 'manager';
    case 'not_awarded':
      return 'not_awarded';
    case 'cancelled':
      return 'cancelled';
    case 'awarded':
      switch (input.lineState) {
        case 'S6':
          return 'released';
        case 'S7':
        case 'S7x':
        case 'S8':
          return 'payroll';
        case 'S9':
          return 'paid';
        case 'S10':
        case 'S11':
          return 'closed';
        case 'X1':
        case 'X2':
          return 'cancelled';
        default:
          // Made at S5, where the Stock Admin team checks its letter (APP-244, APP-247).
          return 'letter';
      }
  }
}

/**
 * Who the next step waits on, from the stage alone (APP-233): one stage, one party, so the stage
 * and who it waits on can never disagree. APP-247 (change 17): the Stock Admin team checks and
 * releases the letter, which MyRewards made (APP-244); the committee checks the earning statement;
 * the letter holder hands out a released letter.
 */
export const SPOT_STAGE_WAITS_ON: Readonly<Record<SpotQueueStage, SpotQueueWaiting>> = {
  committee: 'committee',
  funding: 'executives',
  manager: 'manager',
  no_manager: 'no_manager',
  letter: 'stock_admin',
  released: 'holder',
  payroll: 'payroll',
  paid: 'committee',
  closed: 'none',
  not_awarded: 'none',
  cancelled: 'none',
};

/** Who the next step waits on (`SPOT_STAGE_WAITS_ON`). */
export function spotQueueWaiting(stage: SpotQueueStage): SpotQueueWaiting {
  return SPOT_STAGE_WAITS_ON[stage];
}

/**
 * APP-233: the All Spot awards stage filter. `in_progress` (the default) keeps every stage still in
 * progress, Home's own rule; `all` keeps every stage; a stage keeps only that stage.
 */
export const SPOT_QUEUE_STAGE_FILTERS = ['in_progress', 'all', ...SPOT_QUEUE_STAGES] as const;
export type SpotQueueStageFilter = (typeof SPOT_QUEUE_STAGE_FILTERS)[number];

/** Whether a nominee at this stage shows under this stage filter (APP-233). */
export function spotQueueStagePasses(stage: SpotQueueStage, filter: SpotQueueStageFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'in_progress') return spotInProgress(stage);
  return stage === filter;
}

/**
 * Tom: "At any point in time, someone can reject or cancel … so that they can take action before
 * letters are gone out." A nominee can be cancelled while they wait on the committee or the
 * manager, or once approved until the letter is released (S5). From release on (S6), never.
 */
export function spotCancellable(stage: SpotQueueStage): boolean {
  return (
    stage === 'committee' ||
    stage === 'funding' ||
    stage === 'manager' ||
    stage === 'no_manager' ||
    stage === 'letter'
  );
}
